import { createSelector } from '@reduxjs/toolkit'
import { RootState } from '@/store/store'
import { isoDateStringToUSFormat } from '@/util/time'
import { MedSummaryEntry, UIMedSummaryEntry, UIEntryFieldItem, EntryFieldItem, BillingEntryType } from './schemas'
import _ from 'lodash'

/**
 * Select: Med Summary Entries List by parent ID
 *
 * Returns the list of medical billing summary entries for the given parent (legacy job or
 * living asset), or null if it does not exist.
 * Entries are sorted by service date (ascending), then by their pdf order (ascending) if dates are equal.
 *
 * @returns
 * - `null` - Data has not been loaded yet (key doesn't exist in store)
 * - `[]` - Data was loaded but the parent has no entries (empty array)
 * - `MedSummaryEntry[]` - Data was loaded and contains entries
 */
export const selectMedSummaryEntriesList = createSelector(
  [(state: RootState) => state.medSummaryEntriesState.entries, (_: RootState, props: { parentId: string | undefined | null }) => props.parentId],
  (entries, parentId): MedSummaryEntry[] | null => {
    if (!parentId) return null

    const entriesList = entries[parentId]
    if (entriesList === undefined) return null

    // sort by service date first, with null dates sorted to the end, then by the pdf order (the order the entries appear in the source PDF) when dates are equal
    return _.orderBy(entriesList, [(e) => e.service_date.value ?? '\uffff', (e) => e.pdf_order ?? Infinity], ['asc', 'asc'])
  }
)

/**
 * Helper: Convert EntryFieldItem to UIEntryFieldItem
 */
function toUIEntryFieldItem<T>(item: EntryFieldItem<T>, transformValue: (item: EntryFieldItem<T>) => string | null): UIEntryFieldItem {
  return {
    referenceId: item.reference_id,
    text: transformValue(item),
    updatedAt: item.updated_at,
  }
}

/**
 * Helper: Format amount as a numeric string with thousands separators and 2 decimal places
 * Returns format like "1,234.56" or "-1,234.56" (without dollar sign - added during rendering)
 */
function formatAmount(amount: number | null, entryType: BillingEntryType): string | null {
  if (amount === null) return null
  // BE always sends positive values - apply sign based on entry type
  // Payments and adjustments are always negative, medical bills are positive
  const signedValue = entryType === BillingEntryType.PAYMENT || entryType === BillingEntryType.ADJUSTMENT ? -Math.abs(amount) : Math.abs(amount)

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(signedValue)
}

/**
 * Helper: Format service date for display
 *
 * Converts backend service date to UI-ready format.
 *
 * @param isoDateString - The service date in ISO format (YYYY-MM-DD) or null
 * @returns Formatted date string (MM/DD/YYYY) or null
 */
function formatServiceDate(isoDateString: string | null): string | null {
  if (isoDateString === null) return null
  return isoDateStringToUSFormat(isoDateString)
}

/**
 * Select: UI Med Summary Entries List by parent ID
 *
 * Returns the list of medical billing summary entries transformed to UI-ready format with all preprocessing applied:
 * - Dates formatted as strings (MM/DD/YYYY)
 * - Amounts formatted as currency
 * - All values converted to text strings
 *
 * @returns
 * - `null` - Data has not been loaded yet (key doesn't exist in store)
 * - `[]` - Data was loaded but the parent has no entries (empty array)
 * - `UIMedSummaryEntry[]` - Data was loaded and contains preprocessed entries
 */
export const selectUIMedSummaryEntriesList = createSelector([selectMedSummaryEntriesList], (entriesList): UIMedSummaryEntry[] | null => {
  if (entriesList === null) return null

  return entriesList.map((entry: MedSummaryEntry): UIMedSummaryEntry => {
    return {
      id: entry.id,
      serviceDate: toUIEntryFieldItem(entry.service_date, (item) => formatServiceDate(item.value)),
      facilityName: toUIEntryFieldItem(entry.facility_name, (item) => item.value),
      entryType: entry.entry_type,
      amount: toUIEntryFieldItem(entry.amount, (item) => formatAmount(item.value, entry.entry_type)),
      procedureCode: toUIEntryFieldItem(entry.procedure_code, (item) => item.value),
      filePath: entry.file_path,
      source: entry.source,
      pages: entry.pages,
      comment: entry.comment ?? '',
      createdAt: entry.created_at,
      pdfOrder: entry.pdf_order,
    }
  })
})
